import { PeriodLayout } from "./periodLayout";
import {
  Alert,
  Autocomplete,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
} from "@mui/material";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import {
  AutoRecipeParameterWidget,
  useGetMaterialMetadataSetQuery,
  useGetMaterialPhysicsSetQuery,
  useGetSteelGradeSetQuery,
  useListMaterialConstraintClassesQuery,
} from "store/api/generatedApi";
import { ConditionalLimitsEditor } from "./ConditionalLimitsEditor";
import { ExclusivityRulesEditor } from "./ExclusivityRulesEditor";
import {
  ConditionalInventoryEditor,
  InventoryRulesEditor,
} from "./InventoryRulesEditor";
import {
  ConditionalInventoryTargets,
  ConditionalMaterialLimits,
  PeriodInventoryTargets,
  PeriodMaterialExclusivity,
} from "./ruleParameters";

export const ruleWidgets = [
  "inventory_targets",
  "conditional_inventory_targets",
  "material_exclusivity",
  "material_limit_overrides",
  "steel_grades",
] as const;
export const RuleParameterEditor = ({
  widget,
  value,
  onChange,
  disabled,
  periodLayout,
}: {
  widget: AutoRecipeParameterWidget;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  periodLayout: PeriodLayout;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const materials = useGetMaterialMetadataSetQuery(
    { tenantName, materialMetadataSetId: "default" },
    { skip: widget === "steel_grades" }
  );
  const grades = useGetSteelGradeSetQuery(
    { tenantName, steelGradeSetId: "default" },
    { skip: widget !== "steel_grades" }
  );
  const physics = useGetMaterialPhysicsSetQuery(
    { tenantName, materialPhysicsSetId: "default" },
    { skip: widget !== "material_limit_overrides" }
  );
  const classes = useListMaterialConstraintClassesQuery(
    {
      tenantName,
      materialMetadataSetId: materials.currentData?.id ?? 0,
      materialPhysicsSetId: physics.currentData?.id ?? 0,
    },
    {
      skip:
        widget !== "material_limit_overrides" ||
        !materials.currentData ||
        !physics.currentData,
    }
  );
  const requests =
    widget === "steel_grades"
      ? [grades]
      : widget === "material_limit_overrides"
        ? [materials, physics, classes]
        : [materials];
  if (requests.some((request) => request.isError && !request.currentData))
    return <Alert severity="error">{t("errorLoadingData")}</Alert>;
  if (requests.some((request) => !request.currentData))
    return <CircularProgress />;
  const names = [...(materials.currentData?.materials ?? [])]
    .sort((first, second) => first.ordering - second.ordering)
    .map((material) => material.name);
  const rows = Array.isArray(value) ? value : [];
  return (
    <Stack gap={1}>
      {requests.some((request) => request.isFetching) ? (
        <LinearProgress />
      ) : null}
      {widget === "steel_grades" ? (
        <Autocomplete
          multiple
          options={[
            ...new Set([
              ...(grades.currentData?.steel_grades.map((grade) => grade.name) ??
                []),
              ...(rows as string[]),
            ]),
          ]}
          value={rows as string[]}
          disabled={disabled}
          onChange={(_, grades) => {
            onChange(grades);
          }}
          renderInput={(props) => (
            <TextField {...props} label={t("autoRailGrades")} />
          )}
        />
      ) : widget === "inventory_targets" ? (
        <InventoryRulesEditor
          rules={rows as PeriodInventoryTargets[]}
          materials={names}
          onChange={onChange}
          disabled={disabled}
          periodLayout={periodLayout}
        />
      ) : widget === "conditional_inventory_targets" ? (
        <ConditionalInventoryEditor
          rules={rows as ConditionalInventoryTargets[]}
          materials={names}
          onChange={onChange}
          disabled={disabled}
          periodLayout={periodLayout}
        />
      ) : widget === "material_exclusivity" ? (
        <ExclusivityRulesEditor
          periodLayout={periodLayout}
          rules={rows as PeriodMaterialExclusivity[]}
          materials={names}
          onChange={onChange}
          disabled={disabled}
        />
      ) : widget === "material_limit_overrides" ? (
        <ConditionalLimitsEditor
          rules={rows as ConditionalMaterialLimits[]}
          classes={classes.currentData ?? []}
          onChange={onChange}
          disabled={disabled}
        />
      ) : null}
    </Stack>
  );
};
